import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { commissionReportToCsv, downloadCsv } from "@/lib/report-export";
import {
  commissionPeriodToExclusive,
  useCommissionReport,
} from "@/hooks/use-commission-report";
import { useCommissionDeltas } from "@/hooks/use-commission-deltas";

interface Props {
  /** Inklusiv periode (fra ReportShell). Konverteres til RPC-ens halvåpne grense internt. */
  period: { from: string; to: string };
}

/**
 * Grunnlags-CSV for provisjonsrapporten (VIB-1284): én rad per ansatt ×
 * prosjekt (BF reell, splitt, eierandel, sats, provisjon) + sum-rad per
 * ansatt og totalsum, så regnskap kan ettergå utbetalingen uten å åpne
 * hvert prosjekts ProvisjonTab.
 */
export default function CommissionReportExport({ period }: Props) {
  const exclusive = commissionPeriodToExclusive(period);
  const { data } = useCommissionReport(exclusive);
  // VIB-1240: delta-rader (sene endringer etter lukking) inn i samme fil.
  // Review-blocker: handleExport må feile HØYT hvis denne queryen har feilet
  // eller ikke er ferdig lastet ennå — ALDRI stille eksport av et
  // lønnsgrunnlag uten netting-rader (deltas === undefined dekker begge de
  // tilfellene, siden buildCommissionDeltas alltid returnerer et objekt med
  // en (evt. tom) rows-liste ved suksess).
  const { data: deltas, isError: isDeltasError, isFetching: isDeltasFetching } =
    useCommissionDeltas(exclusive);

  const handleExport = () => {
    if (!data) return;
    if (isDeltasError) {
      toast.error(
        "Kunne ikke hente netting-deltaer for perioden. CSV-en er IKKE generert — prøv igjen før eksport.",
      );
      return;
    }
    if (isDeltasFetching || !deltas) {
      toast.error(
        "Netting-deltaer er ikke ferdig lastet ennå. Prøv igjen om et øyeblikk.",
      );
      return;
    }
    const csv = commissionReportToCsv(data, deltas);
    downloadCsv(`provisjon-${period.from}_${period.to}.csv`, csv);
  };

  return (
    <Button
      size="sm"
      variant="outline"
      onClick={handleExport}
      disabled={!data || data.byEmployee.length === 0}
    >
      <SrIcon name="download" className="w-3.5 h-3.5 mr-1.5" />
      Eksporter CSV
    </Button>
  );
}
